/**
 * Operator, Brand and Affiliate pages — which actions an entity offers.
 *
 * Pure rules, kept out of the .tsx so a test can read them without a DOM. What
 * an actions panel shows follows two things only: the state the entity is in
 * and what the signed-in platform user holds (`mine` on
 * GET /v1/platform/permissions). The server checks both again on every
 * request; this only keeps a button off the screen that would be refused, or
 * that makes no sense for where the entity is (a deleted brand has no colour
 * to set, a live operator has nothing to restore).
 */

import { affiliateStatusTargets } from './direct-edit-model.ts';

/** What the viewer may do, from the capabilities the super admin granted. */
export interface Abilities {
  /** Add or edit operators, brands and affiliates. */
  readonly edit: boolean;
  /** Approve, suspend and enable. */
  readonly suspend: boolean;
  /** Delete (soft, reversible). */
  readonly remove: boolean;
  readonly restore: boolean;
  /** Purge after the retention window. */
  readonly purge: boolean;
  /** Platform charges, tiers and settlement marks. */
  readonly configure: boolean;
  /** The super admin alone may purge at once, from any state. */
  readonly superAdmin: boolean;
  /** Whether the viewer holds any capability that changes something. */
  readonly canChangeAnything: boolean;
  readonly loginAs: {
    readonly operatorUser: boolean;
    readonly brandUser: boolean;
    readonly affiliate: boolean;
  };
}

export function abilitiesFrom(mine: Iterable<string>, superAdmin: boolean): Abilities {
  const held = new Set(mine);
  const edit = held.has('entities.edit');
  const suspend = held.has('entities.suspend');
  const remove = held.has('entities.delete');
  const restore = held.has('entities.restore');
  const purge = held.has('entities.purge');
  const configure = held.has('platform.configure');
  const loginAs = {
    operatorUser: held.has('login_as.operator_user'),
    brandUser: held.has('login_as.brand_user'),
    affiliate: held.has('login_as.affiliate'),
  };
  return {
    edit, suspend, remove, restore, purge, configure, superAdmin, loginAs,
    canChangeAnything: edit || suspend || remove || restore || purge || configure
      || loginAs.operatorUser || loginAs.brandUser || loginAs.affiliate,
  };
}

/** What a viewer holding nothing may do — the state before the permissions arrive. */
export const NO_ABILITIES: Abilities = abilitiesFrom([], false);

/** The three lifecycle acts, as the dialog that performs them names them. */
export type LifecycleMode = 'delete' | 'restore' | 'purge' | 'purge-now';

/* ------------------------------------------------------------------ *
 * Operator
 * ------------------------------------------------------------------ */

export type OperatorAction = 'suspend' | 'reactivate' | LifecycleMode;

/**
 * `state` is the operator's lifecycle state. Only LIVE and SUSPENDED are a
 * status change to make from here: PROSPECT and CONFIGURING have nothing
 * running to suspend, and RESTRICTED is the separate arrears mechanism.
 */
export function operatorActions(state: string, a: Abilities): OperatorAction[] {
  if (state === 'PURGED') return [];
  const out: OperatorAction[] = [];
  if (state === 'SOFT_DELETED') {
    if (a.restore) out.push('restore');
    if (a.purge) out.push('purge');
    return out;
  }
  if (state === 'LIVE' && a.suspend) out.push('suspend');
  if (state === 'SUSPENDED' && a.suspend) out.push('reactivate');
  if (a.remove) out.push('delete');
  if (a.superAdmin && a.purge) out.push('purge-now');
  return out;
}

/* ------------------------------------------------------------------ *
 * Brand
 * ------------------------------------------------------------------ */

export type BrandAction = 'set-colour' | 'propose-change' | LifecycleMode;

export interface BrandStanding {
  readonly deleted: boolean;
  readonly purged: boolean;
}

export function brandStanding(b: {
  soft_deleted_at?: string | null | undefined;
  purged_at?: string | null | undefined;
}): BrandStanding {
  return { deleted: Boolean(b.soft_deleted_at), purged: Boolean(b.purged_at) };
}

export function brandActions(b: BrandStanding, a: Abilities): BrandAction[] {
  if (b.purged) return [];
  const out: BrandAction[] = [];
  if (b.deleted) {
    if (a.restore) out.push('restore');
    if (a.purge) out.push('purge');
    return out;
  }
  if (a.edit) out.push('set-colour', 'propose-change');
  if (a.remove) out.push('delete');
  if (a.superAdmin && a.purge) out.push('purge-now');
  return out;
}

/* ------------------------------------------------------------------ *
 * Affiliate (one profile with one operator, and the person behind it)
 * ------------------------------------------------------------------ */

export type AffiliateAction =
  | 'deactivate' | 'activate'
  | 'suspend-everywhere' | 'reactivate-everywhere'
  | 'login-as'
  | LifecycleMode;

export interface AffiliateStanding {
  /** This profile's status with its operator. */
  readonly status: string;
  /** The person's own status across every operator. */
  readonly identityStatus: string;
  readonly deleted: boolean;
  readonly purged: boolean;
}

export function affiliateStanding(a: {
  status: string;
  identity_status: string;
  deleted_at?: string | null | undefined;
  purged_at?: string | null | undefined;
}): AffiliateStanding {
  return {
    status: a.status,
    identityStatus: a.identity_status,
    deleted: Boolean(a.deleted_at),
    purged: Boolean(a.purged_at),
  };
}

/**
 * Status changes are offered only between ACTIVE and DEACTIVATED, the two the
 * platform may flip; every other state is the operator's own to change. A
 * deleted profile is switched off already, so no status change and no login
 * as — only the person-wide suspension, which is about who they are rather
 * than this profile, stays.
 */
export function affiliateActions(s: AffiliateStanding, a: Abilities): AffiliateAction[] {
  if (s.purged) return [];
  const out: AffiliateAction[] = [];
  if (!s.deleted) {
    if (a.suspend && s.status === 'ACTIVE') out.push('deactivate');
    if (a.suspend && s.status === 'DEACTIVATED') out.push('activate');
  }
  if (a.suspend) out.push(s.identityStatus === 'ACTIVE' ? 'suspend-everywhere' : 'reactivate-everywhere');
  if (!s.deleted && a.loginAs.affiliate) out.push('login-as');
  if (s.deleted) {
    if (a.restore) out.push('restore');
    if (a.purge) out.push('purge');
  } else {
    if (a.remove) out.push('delete');
    if (a.superAdmin && a.purge) out.push('purge-now');
  }
  return out;
}

/**
 * Direct edits on one affiliate profile (Rule Book 3.9d), for a viewer who
 * holds the edit capability: a status move (only when the state machine has
 * one to offer), the operator's own fields, tags, the manager and a note. A
 * deleted or purged profile takes none.
 */
export type AffiliateEdit = 'edit-status' | 'edit-profile' | 'edit-tags' | 'edit-manager' | 'add-note';

export function affiliateEditActions(s: AffiliateStanding, a: Abilities): AffiliateEdit[] {
  if (s.deleted || s.purged) return [];
  const out: AffiliateEdit[] = [];
  // A status change is the suspend capability's, not edit's (review, 2026-10-07).
  if (a.suspend && affiliateStatusTargets(s.status).length > 0) out.push('edit-status');
  if (a.edit) out.push('edit-profile', 'edit-tags', 'edit-manager', 'add-note');
  return out;
}

/** A live brand's on/off switch, for a viewer who holds the suspend capability. */
export function brandStatusAction(b: BrandStanding, status: string, a: Abilities): 'activate' | 'deactivate' | null {
  if (!a.suspend || b.deleted || b.purged) return null;
  return status === 'ACTIVE' ? 'deactivate' : 'activate';
}

/* ------------------------------------------------------------------ *
 * Lists: filters and rows for export
 * ------------------------------------------------------------------ */

export type OperatorStateFilter = 'all' | 'live' | 'setup' | 'suspended' | 'awaiting' | 'deleted';

/** Which filter a row falls under — a row can match "awaiting" as well as its state. */
export function operatorMatches(
  o: { lifecycle_state: string; approval_status: string },
  filter: OperatorStateFilter,
): boolean {
  switch (filter) {
    case 'all': return true;
    case 'live': return o.lifecycle_state === 'LIVE';
    case 'setup': return ['PROSPECT', 'CONFIGURING'].includes(o.lifecycle_state);
    case 'suspended': return ['SUSPENDED', 'RESTRICTED', 'ABANDONED'].includes(o.lifecycle_state);
    case 'awaiting': return o.approval_status === 'PENDING';
    case 'deleted': return ['SOFT_DELETED', 'PURGED'].includes(o.lifecycle_state);
  }
}

export type BrandStateFilter = 'all' | 'active' | 'inactive' | 'deleted';

export function brandMatches(
  b: { status: string; soft_deleted_at?: string | null | undefined; purged_at?: string | null | undefined },
  filter: BrandStateFilter,
): boolean {
  const gone = Boolean(b.soft_deleted_at) || Boolean(b.purged_at);
  switch (filter) {
    case 'all': return true;
    case 'active': return !gone && b.status === 'ACTIVE';
    case 'inactive': return !gone && b.status !== 'ACTIVE';
    case 'deleted': return gone;
  }
}

export type AffiliateStateFilter = 'all' | 'active' | 'pending' | 'inactive' | 'suspended' | 'deleted';

/**
 * A row is a person, who can hold several operator profiles, so a filter
 * matches when any of their profiles does ("suspended" is the person's own
 * platform-wide suspension).
 */
export function affiliateMatches(
  row: {
    identity_status: string;
    operators: readonly { status: string; deleted?: boolean; purged?: boolean }[];
  },
  filter: AffiliateStateFilter,
): boolean {
  const live = row.operators.filter((p) => !p.deleted && !p.purged);
  switch (filter) {
    case 'all': return true;
    case 'active': return row.identity_status === 'ACTIVE' && live.some((p) => p.status === 'ACTIVE');
    case 'pending': return live.some((p) => p.status === 'PENDING' || p.status === 'UNDER_REVIEW');
    case 'inactive': return live.some((p) => !['ACTIVE', 'PENDING', 'UNDER_REVIEW'].includes(p.status));
    case 'suspended': return row.identity_status !== 'ACTIVE';
    case 'deleted': return row.operators.some((p) => p.deleted || p.purged);
  }
}

/**
 * Why an actions panel is empty, so it can say so rather than show a blank:
 * the record is gone for good, the viewer may only look, or nothing applies in
 * the state it is in.
 */
export function whyNoActions(purged: boolean, a: Abilities): 'purged' | 'read-only' | 'none' {
  if (purged) return 'purged';
  return a.canChangeAnything ? 'none' : 'read-only';
}
